import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldOff } from "lucide-react";
import { api, money, shortDate } from "../lib/api";
import { PageHeader, Panel, NeonButton } from "../components/common";

const REC_C = { refund: "#FFB800", contact: "#CCFF00", let_file: "#37E5D7" };
const ST_C = { open: "#FF2BD6", refunded: "#FFB800", contacted: "#CCFF00", declined: "#888" };

const Row = ({ i, onDone }) => {
  const act = async (action) => {
    if (!window.confirm(`${action.replace("_", " ").toUpperCase()} for ${i.customer_name} · $${i.amount}?`)) return;
    try { const { data } = await api.post(`/intercepts/${i.id}/resolve`, { action }); toast.success(`${data.status}${data.dry_run ? " (dry-run)" : ""} · est. saved $${data.estimated_savings}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <tr className="border-b border-divider text-xs align-top" data-testid={`intercept-row-${i.id}`}>
      <td className="py-3 pr-3 font-mono text-[11px]"><span className="uppercase text-white">{i.provider}</span><div className="text-white/35">{shortDate(i.received_at)}</div></td>
      <td className="pr-3">{i.customer_name}<div className="text-white/35">{i.issuer_bank}</div></td>
      <td className="pr-3 font-mono">{money(i.amount)}</td>
      <td className="pr-3 font-mono">{i.predicted_win}%<div className="text-white/35">{i.evidence_count} ev · {i.has_consent ? "consent" : "no consent"}</div></td>
      <td className="pr-3 max-w-[260px]"><span className="font-mono uppercase" style={{ color: REC_C[i.recommendation] }}>{i.recommendation.replace("_", " ")}</span><div className="text-white/50">{i.why}</div></td>
      <td className="pr-3 font-mono uppercase" style={{ color: ST_C[i.status] }}>{i.status}{i.dry_run ? " · dry" : ""}</td>
      <td className="whitespace-nowrap">{i.status === "open" && ["refund", "contact", "let_file"].map((a) => (
        <button key={a} onClick={() => act(a)} data-testid={`intercept-${a}-${i.id}`} className="font-mono text-[10px] uppercase mr-2 px-2 py-1 border border-white/15 hover:border-brand hover:text-brand transition-colors" style={a === i.recommendation ? { borderColor: REC_C[a], color: REC_C[a] } : {}}>{a.replace("_", " ")}</button>
      ))}</td>
    </tr>
  );
};

export default function Intercepts() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [rings, setRings] = useState([]);
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/intercepts"), api.get("/intercepts/stats"), api.get("/fraud/rings")]);
    setItems(a.data); setStats(b.data); setRings(c.data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  const block = async (r) => { await api.post("/fraud/block", { kind: r.field, value: r.value }); toast.success("Blocked at checkout"); load(); };

  return (
    <div data-testid="intercepts-page">
      <PageHeader eyebrow="// pre-dispute intercept · ethoca / verifi alerts (requires an alert provider account)" title="Intercepts">
      </PageHeader>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 border-l border-t border-line bg-surface" data-testid="intercept-stats">
          {[["Alerts", stats.alerts, "#fff"], ["Open (24h)", stats.open, "#FF2BD6"], ["Prevented", stats.prevented, "#CCFF00"], ["Refunds issued", money(stats.refunds_issued), "#FFB800"], ["Contacted", stats.contacted, "#37E5D7"], ["Est. savings", money(stats.savings), "#CCFF00"]].map(([k, v, c]) => (
            <div key={k} className="p-5 border-r border-b border-line"><p className="label-xs">{k}</p><p className="font-mono text-2xl font-extrabold mt-2" style={{ color: c }}>{v}</p></div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-12 gap-6 mt-6">
        <Panel title="Pre-dispute alerts · refund vs fight decision engine" className="col-span-12 xl:col-span-8">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[860px]" data-testid="intercepts-table">
              <thead><tr className="label-xs">{["Provider", "Cardholder", "Amount", "Win odds", "Recommendation", "Status", "Act"].map((h) => <th key={h} className="pb-3 font-normal pr-3">{h}</th>)}</tr></thead>
              <tbody>{items.map((i) => <Row key={i.id} i={i} onDone={load} />)}</tbody>
            </table>
            {items.length === 0 && <p className="font-mono text-xs text-white/35 py-4">No alerts yet. Alerts appear here once an Ethoca or Verifi account is connected.</p>}
          </div>
        </Panel>
        <Panel title="Fraud ring radar · shared IP / device" className="col-span-12 xl:col-span-4 self-start" testid="fraud-ring-panel">
          {rings.length === 0 && <p className="font-mono text-xs text-white/35">No linked disputes detected.</p>}
          <ul className="space-y-3">
            {rings.map((r) => (
              <li key={r.field + r.value} className="border border-[#F87171]/30 p-3" data-testid={`ring-${r.value}`}>
                <p className="font-mono text-[11px] text-[#F87171]">{r.count} disputes · {r.field.replace("_", " ")} {r.value}</p>
                <p className="text-xs text-white/60 mt-1">{money(r.exposure)} exposure · {r.customers.join(", ")}</p>
                <p className="font-mono text-[10px] text-white/40 mt-1">{r.disputes.map((d) => <Link key={d} to={`/app/disputes/${d}`} className="mr-2 hover:text-brand">{d}</Link>)}</p>
                {r.blocked ? <p className="font-mono text-[10px] text-brand mt-2">BLOCKED AT CHECKOUT</p>
                  : <NeonButton variant="danger" className="mt-2" onClick={() => block(r)} data-testid={`ring-block-${r.value}`}><ShieldOff className="w-3 h-3" /> Block + alert issuers</NeonButton>}
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
